// angular
//   .module('HeroPRO')
//   .factory('FRClosingFactory', fundingRequestClosingFactory);

import _ from 'lodash';

/* @ngInject */
export default function fundingRequestClosingFactory(HPConfig, $http, $stateParams, $q, FRInformationFactory, ProjectAdvanceFactory, HPStateMachineConfig, HPPipelineStages) {
    let _fundingRequestClosingPromise;
    let _projectDetails = {};
    let _paymentInstructions = {};
    let _fundingRequest = {};
    let _eSignDocuments = {};
    let _projectDetailsConfirmed = false;
    let _paymentInstructionsConfirmed = false;
    let _isFundingScheduled = false;
    let _stateMachineData = {};
    let _projectAdvanceDetails = {};

    return {
        getFundingRequestClosing,
        getFundingRequestClosingPromise,
        setProjectDetails,
        getProjectDetails,
        setProjectDetailsConfirmed,
        getProjectDetailsConfirmed,
        setPaymentInstructions,
        getPaymentInstructions,
        getPaymentInstructionsConfirmed,
        getFundingRequest,
        getESignDocuments,
        getIsFundingScheduled,
        confirmProjectComplete,
        confirmProjectDetails,
        confirmPaymentInstructions,
        submitProjectForFunding,
        assignAttachmentToConditions,
        getProjectAdvanceDetails,
        commitAttachment,
    };

    function getFundingRequestClosing() {
        _stateMachineData = FRInformationFactory.getStateMachineData();

        _fundingRequestClosingPromise = $q((resolve) => {
            FRInformationFactory.getFundingRequestInformationPromise().then(() => {
                // Once the fundingRequestInformationPromise has resolved, check whether closing information needs to be retrieved
                if (getClosingInfo(_stateMachineData) === true) {
                    // Closing information needs to be retrieved, wait for the funding request closing API call to finish and then resolve _fundingRequestClosingPromise
                    // Project Advance information also needs to be retrieved
                    resetFundingRequestClosing();
                    $http.get(`${HPConfig.heroProApi}Loan/FundingRequest/ProjectClosingInfo`, { params: getFundingRequestClosingParams() }).then((response) => {                        
                        setProjectDetails(_.omit(response.data.Data.ProjectDetails, 'FundingRequestId', 'LoanStateId'));
                        setProjectDetailsConfirmed();
                        setFundingRequest(_.omit(response.data.Data.FundingRequest, 'FundingRequestId'));
                        setPaymentInstructions(_.omit(response.data.Data.PaymentInstructions, 'FundingRequestId', 'LoanStateId'));
                        setPaymentInstructionsConfirmed();
                        setESignDocuments(response.data.Data.ESignDocuments);
                        setIsFundingScheduled();

                        _projectAdvanceDetails = response.data.Data.ProjectAdvance;

                        resolve();
                    });
                } else {
                    // Closing information does not need to be retrieved, reset local variables to defaults then resolve _fundingRequestClosingPromise
                    resetFundingRequestClosing();
                    resolve();
                }
            });
        });
        return _fundingRequestClosingPromise;
    }

    function getFundingRequestClosingPromise() {
        return _fundingRequestClosingPromise;
    }

    function resetFundingRequestClosing() {
        _projectDetails = {};
        _paymentInstructions = {};
        _fundingRequest = {};
        _eSignDocuments = {};
        _projectDetailsConfirmed = false;
        _paymentInstructionsConfirmed = false;
        _isFundingScheduled = false;
        _projectAdvanceDetails = {};
    }

    function getFundingRequestClosingParams() {
        return {
            FundingRequestId: $stateParams.appId,
            FilterByConditionTypeCategoryIds: 4,
            ExcludeClearedConditions: false,
            ExcludeWaivedConditions: true,
            IncludePremierTierData: true,
            // The following parameters can be toggled depending on what data is needed based on the state of the funding request
            includeProjectDetails: true,
            includePaymentDetails: true,
            includeConditions: true,

        };
    }

    function setProjectDetails(newProjectDetails) {
        _.extend(_projectDetails, newProjectDetails);
    }

    function getProjectDetails() {
        return _projectDetails;
    }

    function setProjectDetailsConfirmed() {
    // NOTE: The right side of the OR clause is temporary. It can be removed once there are no more active loans that have been processed in COPO. - MS 1/15/16
        _projectDetailsConfirmed = (!!_projectDetails.ProjectDetailsVerificationStatusId && _projectDetails.ProjectDetailsVerificationStatusId === 2) || _stateMachineData.CompletionCertificateStateId >= 4;
    }

    function getProjectDetailsConfirmed() {
        return _projectDetailsConfirmed;
    }

    function setPaymentInstructions(newPaymentInstructions) {
        _.extend(_paymentInstructions, newPaymentInstructions);
        const disbursements = _paymentInstructions.Disbursements || [];
        let newDisbursements = [
            { PayeeType: 'Contractor', DisbursementAmount: 0 },
            { PayeeType: 'Property Owner', DisbursementAmount: 0 },
            { PayeeType: 'Other', DisbursementAmount: 0 },
        ];
        let advancePayment = null;
        const defaultPayeeTypeId = getFundingRequest().FundingRequestDetail.DefaultPayeeTypeId;
        const fundingDisbursementModelTypeId = getFundingRequest().FundingRequestDetail.FundingDisbursementModelTypeId;
        const isDisbursementLocked = getFundingRequest().FundingRequestDetail.IsDisbursementLocked;

        // Look for any Advance Payment disbursements (CCDisbursementTypeId === 3)
        for (let i = 0; i < disbursements.length; i++) {
            if (disbursements[i].CCDisbursementTypeId === 3) {
                advancePayment = disbursements[i];
                break;
            }
        }

        // Check if there are any HERO Funding disbursements avaiable (CCDisbursementTypeId === 2)
        const heroDisbursements = _.filter(disbursements, disbursement => disbursement.CCDisbursementTypeId === 2);

        if (heroDisbursements.length > 0) {
            // Use the HERO Funding disbursements returned from the server
            _.each(heroDisbursements, (disbursement) => {
                if (fundingDisbursementModelTypeId === 11) {
                    // Purchase PEFI - Always assign the disbursement to the contractor regardless of what the server returns
                    newDisbursements[0] = disbursement;
                    newDisbursements[0].PayeeType = 'Contractor';
                } else if (disbursement.PayeeType === 'Contractor') {
                    newDisbursements[0] = disbursement;
                    // If there is a contractor disbursement and an AP set their sum as the contractor disbursement amount
                    if (advancePayment !== null) {
                        newDisbursements[0].DisbursementAmount = newDisbursements[0].DisbursementAmount + advancePayment.DisbursementAmount;
                    }
                } else if (disbursement.PayeeType === 'Property Owner') {
                    newDisbursements[1] = disbursement;
                } else {
                    newDisbursements[2] = disbursement;
                }
            });
        } else {
            // Set disbursement defaults since no HERO Funding disbursements were returned from the server
            if (fundingDisbursementModelTypeId === 11) {
                // Purchase PEFI - Always set the default to contractor
                newDisbursements[0] = {
                    PayeeType: 'Contractor',
                    DisbursementAmount: _paymentInstructions.InitialRequested,
                };
            } else if (defaultPayeeTypeId === 1) {
                newDisbursements[0] = {
                    PayeeType: 'Contractor',
                    DisbursementAmount: _paymentInstructions.InitialRequested,
                };
            } else if (defaultPayeeTypeId === 2) {
                newDisbursements[2] = {
                    PayeeType: 'Other',
                    DisbursementAmount: _paymentInstructions.InitialRequested,
                };
            }
        }

        _paymentInstructions.PremierTierDetail = getFundingRequest().PremierTierDetail;

        // If disbursements are locked, remove disbursements that have a value of 0
        if (isDisbursementLocked === true) {
            newDisbursements = _.reject(newDisbursements, disbursement => disbursement.DisbursementAmount === 0);
        }

        _paymentInstructions.newDisbursements = newDisbursements;
        _paymentInstructions.advancePayment = advancePayment;

        // used to show deductions, this is for dealer fee CCDisbursement amount discrepancy before and after funding
        _paymentInstructions.isProjectFunded = FRInformationFactory.getStateMachineData().FundingRequestStateId === HPStateMachineConfig.FR.HERO_Funded ||
        FRInformationFactory.getStateMachineData().FundingRequestStateId === HPStateMachineConfig.FR.HERO_Funding_Scheduled ||
        FRInformationFactory.getStateMachineData().FundingRequestStateId === HPStateMachineConfig.FR.HLP_Completed ||
        FRInformationFactory.getStateMachineData().FundingRequestStateId === HPStateMachineConfig.FR.HLP_Recording_Scheduled;
    }

    function getPaymentInstructions() {
        return _paymentInstructions;
    }

    function setPaymentInstructionsConfirmed() {
    // NOTE: The right side of the OR clause is temporary. It can be removed once there are no more active loans that have been processed in COPO. - MS 1/15/16
        _paymentInstructionsConfirmed = (!!_paymentInstructions.PaymentVerificationStatusId && _paymentInstructions.PaymentVerificationStatusId === 2) || _stateMachineData.CompletionCertificateStateId >= 4;
    }

    function getPaymentInstructionsConfirmed() {
        return _paymentInstructionsConfirmed;
    }

    function setFundingRequest(newFundingRequest) {
        _.extend(_fundingRequest, newFundingRequest);
    }

    function getFundingRequest() {
        return _fundingRequest;
    }

    function setESignDocuments(newESignDocuments) {
        _.extend(_eSignDocuments, newESignDocuments);
    }

    function getESignDocuments() {
        return _eSignDocuments;
    }

    function getClosingInfo(stateMachineData) {
    // stateMachineData.LoanStateId === HPStateMachineConfig.Loan.Product_And_Price_Added ||
        return stateMachineData.FinDocsStateId === HPStateMachineConfig.FD.Fin_Docs_Created ||
   stateMachineData.FinDocsStateId === HPStateMachineConfig.FD.Fin_Docs_Approved_By_CO ||
   stateMachineData.FinDocsStateId === HPStateMachineConfig.FD.Fin_Docs_Emailed_To_PO ||
   stateMachineData.FinDocsStateId === HPStateMachineConfig.FD.Fin_Docs_eSign_Initiated_By_PO ||
   stateMachineData.FinDocsStateId === HPStateMachineConfig.FD.Fin_Docs_Signed_By_PO ||
   stateMachineData.FinDocsStateId === HPStateMachineConfig.FD.Fin_Docs_Signed_By_All_PO ||
   stateMachineData.FinDocsStateId === HPStateMachineConfig.FD.Issuer_Reviewed ||
   stateMachineData.FinDocsStateId === HPStateMachineConfig.FD.Issuer_Countersigned ||
   stateMachineData.LoanStateId === HPStateMachineConfig.Loan.Final ||
   stateMachineData.PipelineStageId === HPPipelineStages.Closed.pipelineStageId;
    }

    function setIsFundingScheduled() {
        _isFundingScheduled = (!!_fundingRequest && !!_fundingRequest.FundingRequestDetail && !!_fundingRequest.FundingRequestDetail.ScheduledDisbursementDate);
    }

    function getIsFundingScheduled() {
        return _isFundingScheduled;
    }


    function getProjectAdvanceDetails() {
        return _projectAdvanceDetails;
    }


    function confirmProjectComplete() {
        return $http({
            url: `${HPConfig.heroProApi}Loan/FundingRequest/ConfirmProjectComplete`,
            method: 'POST',
            data: { FundingRequestId: $stateParams.appId },
        });
    }

    function confirmProjectDetails() {
        return $http({
            url: `${HPConfig.heroProApi}Loan/FundingRequest/ConfirmProjectDetails`,
            method: 'POST',
            data: { FundingRequestId: $stateParams.appId },
        });
    }

    function confirmPaymentInstructions(params) {
        return $http({
            url: `${HPConfig.heroProApi}Loan/FundingRequest/ConfirmPaymentInstructions`,
            method: 'POST',
            data: params,
        });
    }

    function submitProjectForFunding(params) {
        return $http({
            url: `${HPConfig.heroProApi}Case/Create`,
            method: 'POST',
            data: params,
        });
    }

    function assignAttachmentToConditions(params) {
        return $http({
            url: `${HPConfig.heroProApi}Conditions/Attachment/Assign`,
            method: 'POST',
            data: params,
        });
    }

    function commitAttachment(params) {
        return $http({
            url: `${HPConfig.heroProApi}DocumentManagement/CommitAttachment/`,
            method: 'POST',
            data: params,
        });
    }
}



// WEBPACK FOOTER //
// ./src/app/appDetail/fundingRequestClosingFactory.js